import { API_BASE_URL } from '@/config/api';
import { defaultAppPromotionSettings, type AppPromotionSettings } from '@/config/appPromotion';
import { getAppStoreUrl } from '@/lib/appStoreUrls';
import { apiService } from '@/services/apiService';

export function normalizePromotion(settings: AppPromotionSettings | null): AppPromotionSettings {
  return { ...defaultAppPromotionSettings, ...settings, storeUrls: { ...defaultAppPromotionSettings.storeUrls, ...settings?.storeUrls } };
}

type PromotionResponse = { settings: AppPromotionSettings | null };

async function readResponse(response: Response): Promise<PromotionResponse> {
  if (!response.ok) throw new Error(`App promotion request failed (${response.status})`);
  return response.json() as Promise<PromotionResponse>;
}

export async function fetchPublicAppPromotion(): Promise<AppPromotionSettings> {
  try {
    const response = await fetch(`${API_BASE_URL}/read/v1/service/app-promotion`);
    const { settings } = await readResponse(response);
    return normalizePromotion(settings);
  } catch {
    return defaultAppPromotionSettings;
  }
}

export async function fetchAdminAppPromotion(): Promise<AppPromotionSettings> {
  const response = await fetch(`${API_BASE_URL}/api/admin/app-promotion`, {
    headers: { token: apiService.getToken() ?? '' },
  });
  const { settings } = await readResponse(response);
  return normalizePromotion(settings);
}

export async function saveAdminAppPromotion(settings: AppPromotionSettings): Promise<AppPromotionSettings> {
  settings = normalizePromotion(settings);
  for (const [key, value] of Object.entries(settings.storeUrls)) {
    if (value && !getAppStoreUrl(value, key as keyof AppPromotionSettings['storeUrls'])) throw new Error('Use the complete Google Play or App Store app listing link.');
  }
  const response = await fetch(`${API_BASE_URL}/api/admin/app-promotion`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', token: apiService.getToken() ?? '' },
    body: JSON.stringify(settings),
  });
  const saved = await readResponse(response);
  if (!saved.settings) throw new Error('The server did not return saved settings');
  return normalizePromotion(saved.settings);
}
